import { GlobalDialog } from '_/components/GlobalDialog'
import React, { createContext, useContext, useRef, useCallback, useReducer } from 'react'

const DialogContext = createContext()

export const DEFAULT_DIALOG_INFO = {
  isOpen: false,
  type: 'info',
  subject: '',
}

export const DialogProvider = ({ children }) => {
  const dialogRef = useRef(DEFAULT_DIALOG_INFO)
  const [, forceUpdate] = useReducer(x => x + 1, 0)

  const showDialog = useCallback(
    dialogProps => {
      dialogRef.current = { isOpen: true, ...dialogProps }
      forceUpdate()
    },
    [forceUpdate]
  )

  const stackDialog = useCallback(
    dialogProps => {
      const currentDialog = { ...dialogRef.current }
      dialogRef.current = {
        isOpen: true,
        ...dialogProps,
        onCancel: () => showDialog(currentDialog),
      }
      forceUpdate()
    },
    [showDialog]
  )

  const closeDialog = useCallback(() => {
    dialogRef.current = DEFAULT_DIALOG_INFO
    forceUpdate()
  }, [forceUpdate])

  const renderDialog = dialogRef.current?.isOpen ? <GlobalDialog {...dialogRef.current} /> : null

  return (
    <DialogContext.Provider value={{ showDialog, closeDialog, stackDialog }}>
      {children}
      {renderDialog}
    </DialogContext.Provider>
  )
}

/**
 * @typedef {Object} DialogProps
 * @property {'success' | 'error' | 'info' | 'warning'} type - The type of the dialog.
 * @property {React.ElementType} [customIcon] - The custom icon component for the dialog.
 * @property {string} subject - The subject of the dialog.
 * @property {string} [description] - The description of the dialog.
 * @property {string} labelApprove - The label for the approve button.
 * @property {string} [labelCancel] - The label for the cancel button.
 * @property {function} onApprove - The function to call when the approve button is clicked.
 * @property {function} [onCancel] - The function to call when the cancel button is clicked.
 */

/**
 * @typedef {Object} DialogContextValue
 * @property {function(DialogProps): void} showDialog - Shows the dialog with the specified properties.
 * @property {function(): void} closeDialog - Closes the dialog.
 * @property {function(DialogProps): void} stackDialog - Stacks the dialog on top of the current dialog.
 */

/**
 * Use the dialog context.
 *
 * @returns {DialogContextValue} The dialog context value.
 */
export const useDialog = () => {
  const context = useContext(DialogContext)
  if (!context) {
    throw new Error('useDialog must be used within a DialogProvider')
  }
  return context
}
